/**
 * Service Requests API
 * Create and manage service requests (partner auth required)
 */

import { apiClient } from './apiClient';

export interface CustomerDetails {
  name?: string;
  phone?: string;
  email?: string;
  address?: string;
  pincode?: string;
}

export interface CreateServiceRequestPayload {
  serviceId: string;
  customerDetails?: CustomerDetails;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  documents?: string[];
}

export interface ServiceRequest {
  _id: string;
  requestId: string;
  partnerId: string;
  customerId?: string;
  serviceId: { _id: string; name: string; category: string; basePrice?: number };
  status: 'pending' | 'assigned' | 'inProgress' | 'completed' | 'rejected';
  priority: string;
  documents?: string[];
  cost?: { estimated?: number; actual?: number };
  createdAt: string;
  updatedAt: string;
}

export interface ServiceRequestsResponse {
  requests: ServiceRequest[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Create a new service request (partner auth required)
 */
export async function createServiceRequest(
  payload: CreateServiceRequestPayload
): Promise<ServiceRequest> {
  const response = await apiClient.post<ServiceRequest>(
    '/loyalty/service-requests',
    payload
  );

  if (response.success && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to create service request');
}

/**
 * Get service requests for the logged-in partner (partner auth required)
 */
export async function getServiceRequests(params?: {
  status?: string;
  page?: number;
  limit?: number;
}): Promise<ServiceRequestsResponse> {
  const searchParams: Record<string, string> = {};
  if (params?.status) searchParams.status = params.status;
  if (params?.page) searchParams.page = String(params.page);
  if (params?.limit) searchParams.limit = String(params.limit);

  const response = await apiClient.get<ServiceRequestsResponse>(
    '/loyalty/service-requests',
    searchParams
  );

  if (response.success && response.data) {
    const d = response.data as any;
    return {
      requests: d.requests || [],
      total: d.pagination?.total ?? d.total ?? 0,
      page: d.pagination?.page ?? d.page ?? 1,
      limit: d.pagination?.limit ?? d.limit ?? 20,
      totalPages: d.pagination?.pages ?? d.totalPages ?? 1,
    };
  }

  throw new Error(response.message || 'Failed to fetch service requests');
}

/**
 * Get a single service request by ID (partner auth required)
 */
export async function getServiceRequestById(id: string): Promise<ServiceRequest> {
  const response = await apiClient.get<ServiceRequest>(
    `/loyalty/service-requests/${id}`
  );

  if (response.success && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to fetch service request');
}
